import { Address, PayerContact } from '../../api';
import { PayerDataKind } from './payer-routing';

/**
 * Setting a payer's paper destination — the inverse of `getPaperPath`.
 *
 * The read side DERIVES one channel per category by preference (fax, then email, then post),
 * because a payer may have several contact details and only one of them is where the paperwork
 * actually goes. The editor has to work the other way round: an operator picks a channel and
 * types a destination.
 *
 * Those two halves have to agree, and that is the whole reason this module exists. `PayerContact`
 * holds all four channels per category at once, so a payer with a fax AND an address displays as
 * Fax whatever the operator selected. **Choosing a channel therefore CLEARS the others for that
 * category** — otherwise the editor would show Letter, the panel would show Fax, and the claim
 * would go by fax. A UI that disagrees with the routing it configures is worse than no UI.
 *
 * `phone` is deliberately untouched. It is a contact detail, not a destination — nothing routes
 * paperwork to a phone number, and clearing it when someone sets a fax would delete information
 * an operator put there on purpose.
 */

/**
 * The categories X12 cannot carry, so they are paper whatever the clearinghouse supports.
 *
 * No transaction exists for an appeal, an overpayment or general correspondence — that is a
 * property of the standard, not of a payer's setup (NS-7). Distinct from `isAlwaysPaper`,
 * which asks whether a whole PAYER has no electronic route at all.
 *
 * Only reconsiderations are REQUIRED: an appeal has nowhere else to go, which is why the
 * import template marks `appeals_channel` and `appeals_value` required too. The other two are
 * paper by definition but may legitimately be unset.
 */
export const PAPER_ONLY_KINDS: PayerDataKind[] = ['reconsideration', 'overpayment', 'other'];

export function isPaperOnlyKind(kind: PayerDataKind): boolean {
    return PAPER_ONLY_KINDS.includes(kind);
}

/** The channels the editor offers. `phone` is not one: you cannot post a claim to it. */
export type PaperChannel = 'Email' | 'Fax' | 'Letter';

/** `PayerContact`'s field suffix for each category. `reconsideration` is `Appeal`. */
const CONTACT_SUFFIX: Record<PayerDataKind, 'Claim' | 'Appeal' | 'Documentation' | 'Overpayment' | 'Other'> = {
    claim: 'Claim',
    reconsideration: 'Appeal',
    paperwork: 'Documentation',
    overpayment: 'Overpayment',
    other: 'Other',
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The API's own rule for a fax number, verbatim: `faxClaim: must match "^\+[1-9]\d{1,14}$"`. */
const E164 = /^\+[1-9]\d{1,14}$/;

/**
 * Turn what an operator types into what the API accepts.
 *
 * Fax numbers are stored in **E.164** — the models say so ("The recipient fax number in
 * E.164") and the server enforces it. Nobody types E.164: they type `866-473-2222`, which is
 * what the placeholder shows and what is printed on a payer's own correspondence.
 *
 * This existed as a bug first. `isValidPaperValue` asked only for ten digits, so a perfectly
 * ordinary fax number passed our guard and the server rejected the write with
 * `faxClaim: must match "^\+[1-9]\d{1,14}$"`. The guard written to stop a half-typed number
 * becoming live routing was itself letting through numbers that could never be saved — and no
 * test caught it, because the only fax case exercised was the one that was SUPPOSED to fail.
 *
 * So: accept what people type, write what the contract requires. Ten digits is assumed North
 * American, which is the only assumption this codebase can make — every payer here is US.
 */
export function normalisePaperValue(channel: PaperChannel, value: string): string {
    if (channel !== 'Fax') {
        return value.trim();
    }

    const trimmed = value.trim();
    if (E164.test(trimmed)) {
        return trimmed;
    }

    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 10) {
        return `+1${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }

    // Anything else is returned as typed, so `isValidPaperValue` rejects it rather than this
    // silently inventing a country code for a number it does not understand (NS-4).
    return trimmed;
}

/**
 * Is this destination usable?
 *
 * Autosave writes on blur, so this is what stands between a half-typed fax number and live
 * routing config. `866-473-22` looks like a fax number and reaches nobody.
 */
export function isValidPaperValue(channel: PaperChannel, value: string | Address | undefined): boolean {
    if (!value) {
        return false;
    }

    if (channel === 'Email') {
        return typeof value === 'string' && EMAIL.test(value.trim());
    }

    if (channel === 'Fax') {
        // Validated on the NORMALISED form, because that is what gets written. Asking only for
        // "ten or more digits" accepted numbers the API rejects outright.
        return typeof value === 'string' && E164.test(normalisePaperValue('Fax', value));
    }

    if (typeof value === 'string') {
        return false;
    }

    /*
     * Every field the API marks required on `Address`, not a guess at a useful subset.
     *
     * `name`, `lines`, `city`, `state` and `postal` are all non-optional on the generated
     * model — only `country` and `type` are optional — and the generated model is derived from
     * the spec (NS-10). This used to check a line and a postal code only, so an address with no
     * State passed here, went out on the wire, and came back rejected. Ken Coleta hit exactly
     * that on 2026-08-31 and could not tell why: the field looked fine and the failure arrived
     * from the server.
     *
     * Checking what the API actually demands turns that round trip into an unfinished form.
     *
     * `name` is the one required field NOT enforced here. It is the attention line — "Claims
     * Department" — and a PO box with no department is a perfectly deliverable address, so
     * blocking a save on it would invent a rule the post office does not have. The generated
     * model says the API wants it; whether it truly rejects without one is unconfirmed, and is
     * the open question for the backend. Everything else here is a deliverable-address rule as
     * well as an API one, which is why those four are safe to enforce.
     */
    const address = value as Address;
    const filled = (v: unknown): boolean => !!v && !!String(v).trim();
    const hasLine = Array.isArray(address.lines) && address.lines.some(line => filled(line));

    return hasLine && filled(address.city) && filled(address.state) && filled(address.postal);
}

/**
 * Set one category's paper destination, returning a NEW contact.
 *
 * Immutable on purpose: the editor autosaves per field, and mutating the object the panel is
 * rendering from would let a half-applied change show before the write lands.
 */
export function setPaperPath(
    contact: PayerContact | undefined,
    kind: PayerDataKind,
    channel: PaperChannel,
    value: string | Address,
): PayerContact {
    const suffix = CONTACT_SUFFIX[kind];
    const next: PayerContact = { ...(contact || ({} as PayerContact)) };

    // Clear every routable channel for this category first, so the chosen one is the one the
    // read side will derive. See the note at the top: whichever survives is what claims follow.
    delete (next as Record<string, unknown>)[`email${suffix}`];
    delete (next as Record<string, unknown>)[`fax${suffix}`];
    delete (next as Record<string, unknown>)[`address${suffix}`];

    if (channel === 'Email') {
        (next as Record<string, unknown>)[`email${suffix}`] = String(value).trim();
    } else if (channel === 'Fax') {
        // Stored in E.164, whatever the operator typed.
        (next as Record<string, unknown>)[`fax${suffix}`] = normalisePaperValue('Fax', String(value));
    } else {
        const address = value as Address;

        /*
         * `country` defaulted, because the API 500s without it.
         *
         * Measured against sandbox on 2026-08-31, four writes to `PUT /support/payer/{id}`:
         * name+coverage alone returned 200; the same call with a complete address and no
         * `country` returned 500 `err.traceable_internal_server`; adding `country: 'US'`
         * returned 200; and an address with an invalid `type` returned a clean 400 naming the
         * field — so the service CAN validate, it just crashes on this one.
         *
         * The generated model marks `country` optional, so nothing in the client would have
         * told us. This is what Ken Coleta hit repeatedly and could not diagnose: a complete,
         * correct address rejected by an internal error with only a trace id.
         *
         * Defaulted rather than added to the form: every payer address on this platform is a
         * US one, and asking an operator for a country to work around a server fault would be
         * making our problem theirs. Reported to the backend — when it returns a 400, or stops
         * caring, this default can go.
         */
        (next as Record<string, unknown>)[`address${suffix}`] = {
            ...address,
            country: address.country || 'US',
        } as Address;
    }

    return next;
}

/**
 * Remove a category's paper destination entirely.
 *
 * For a category with no X12 transaction behind it this makes the data unroutable — which is
 * a real state the design shows ("Not routed"), not an error. The caller decides whether to
 * warn; reconsiderations in particular have nowhere else to go (NS-7).
 */
export function clearPaperPath(contact: PayerContact | undefined, kind: PayerDataKind): PayerContact {
    const suffix = CONTACT_SUFFIX[kind];
    const next: PayerContact = { ...(contact || ({} as PayerContact)) };

    delete (next as Record<string, unknown>)[`email${suffix}`];
    delete (next as Record<string, unknown>)[`fax${suffix}`];
    delete (next as Record<string, unknown>)[`address${suffix}`];

    return next;
}

/** Copy one category's paper destination onto another — the design's "Copy claim submissions". */
export function copyPaperPath(
    contact: PayerContact | undefined,
    from: PayerDataKind,
    to: PayerDataKind,
): PayerContact {
    const source = CONTACT_SUFFIX[from];
    const target = CONTACT_SUFFIX[to];
    const base = contact || ({} as PayerContact);
    const read = (prefix: string): unknown => (base as unknown as Record<string, unknown>)[`${prefix}${source}`];

    // Copying an unset category clears the target rather than leaving it stale — "same as claim
    // submissions" has to keep meaning that when claim submissions is empty.
    const next = clearPaperPath(base, to) as unknown as Record<string, unknown>;
    const email = read('email');
    const fax = read('fax');
    const address = read('address');

    if (fax) {
        next[`fax${target}`] = fax;
    } else if (email) {
        next[`email${target}`] = email;
    } else if (address) {
        next[`address${target}`] = address;
    }

    return next as unknown as PayerContact;
}
